/**
 * Turning an incoming URL into "which screen did this person mean".
 *
 * Deliberately hand-rolled: this runs on Hermes, where `URL` and
 * `URLSearchParams` are partial polyfills with their own edge cases, and it has
 * to survive shapes a spec-compliant parser is not asked about —
 * `flashkaapp://deck/x` (where `deck` parses as the HOST), a bare `/deck/x` from
 * expo-router's native-intent hook, an apex link, a `/de/…` marketing prefix.
 *
 * The one rule: a URL either resolves to exactly one {@link LinkRoute} or to
 * `null`. `null` is not a failure — it is what sends the URL to a browser
 * instead of a not-found screen.
 */

import {
  APP_URL_SCHEME,
  FLASHKA_DEV_LINK_HOST_PATTERNS,
  FLASHKA_LINK_HOSTS,
  FLASHKA_WEB_ORIGIN,
} from './app-identity';
import {
  LEGACY_DECK_SHARE_RE,
  LINK_ROUTES,
  type LinkKind,
  type LinkRoute,
  linkRoute,
} from './link-routes';

/**
 * The 7 prefixed marketing languages (`/de/…`, `/pl/…`). Mirrors
 * `flashka-web/src/seo/marketing-routes.ts` → `PATH_LANGS`; English is served
 * at the root and `/en/*` 301s away.
 *
 * Stripped only when the REMAINDER matches a route, so a future deck named
 * `it` cannot be mistaken for Italian.
 *
 * A COPY, deliberately: `marketing-routes.ts` is edge-runtime code that must
 * stay free of package imports, so it cannot import this and this cannot import
 * it. `marketing-langs.spec.ts` reads that file and fails if the two ever
 * disagree — without it, adding a language would give web `/nl/dashboard` while
 * the app sent the same claimed link to a browser, and nothing would go red.
 */
export const MARKETING_PATH_LANGS = ['it', 'es', 'fr', 'de', 'sv', 'pl', 'he', 'ca'];

export interface ParsedFlashkaLink {
  readonly route: LinkRoute;
  readonly kind: LinkKind;
  /** Decoded dynamic segments, keyed by the pattern's `:name`. */
  readonly params: Readonly<Record<string, string>>;
  /** Decoded query, narrowed to the route's `carry` list. */
  readonly query: Readonly<Record<string, string>>;
  /** The canonical web path this resolved to, without origin or query. */
  readonly path: string;
}

interface SplitUrl {
  scheme: string;
  host: string;
  path: string;
  query: string;
}

/** Tolerant `scheme://host/path?query#hash` split. Returns `null` for junk. */
function splitUrl(raw: string): SplitUrl | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Already a bare path (expo-router hands native-intent this shape for
  // custom-scheme links on some platforms).
  if (trimmed.startsWith('/')) {
    const [beforeHash = ''] = trimmed.split('#');
    const q = beforeHash.indexOf('?');
    return {
      scheme: '',
      host: '',
      path: q === -1 ? beforeHash : beforeHash.slice(0, q),
      query: q === -1 ? '' : beforeHash.slice(q + 1),
    };
  }

  const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):(\/\/)?([^/?#]*)([^?#]*)(?:\?([^#]*))?/.exec(
    trimmed,
  );
  if (!m) return null;
  const [, scheme = '', doubleSlash, authority = '', rest = '', query = ''] = m;

  // `flashkaapp://deck/x` parses `deck` as the authority. For the app's own
  // scheme the authority is really the first path segment — that is how every
  // hand-written `flashkaapp://…` link in the wild is shaped, and
  // `Linking.createURL` emits the triple-slash form that lands here with an
  // empty authority. Both must mean the same thing.
  const isAppScheme = scheme.toLowerCase() === APP_URL_SCHEME;
  if (isAppScheme || !doubleSlash) {
    const joined = authority ? `/${authority}${rest}` : rest;
    return { scheme, host: '', path: joined || '/', query };
  }
  return { scheme, host: authority.toLowerCase(), path: rest || '/', query };
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed escape is data, not a crash — the caller gets the raw text
    // and the route match simply fails if it was meaningful.
    return value;
  }
}

/** `a=1&b=two+words` → `{ a: '1', b: 'two words' }`. Last value wins. */
export function parseQueryString(query: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of query.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const rawKey = eq === -1 ? pair : pair.slice(0, eq);
    const rawValue = eq === -1 ? '' : pair.slice(eq + 1);
    const key = safeDecode(rawKey.replace(/\+/g, ' '));
    if (!key) continue;
    out[key] = safeDecode(rawValue.replace(/\+/g, ' '));
  }
  return out;
}

/** Inverse of {@link parseQueryString}; drops empty values so links stay clean. */
export function stringifyQuery(query: Record<string, string | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  return parts.join('&');
}

function splitSegments(path: string): string[] {
  return path.split('/').filter((segment) => segment.length > 0);
}

function matchPattern(
  pattern: string,
  segments: string[],
): Record<string, string> | null {
  const patternSegments = splitSegments(pattern);
  if (patternSegments.length !== segments.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < patternSegments.length; i += 1) {
    const patternSegment = patternSegments[i];
    const segment = segments[i];
    if (patternSegment.startsWith(':')) {
      if (!segment) return null;
      params[patternSegment.slice(1)] = safeDecode(segment);
      continue;
    }
    if (patternSegment !== segment) return null;
  }
  return params;
}

/**
 * The path half of the resolution: first row in {@link LINK_ROUTES} whose
 * pattern matches, in table order.
 */
export function matchLinkPath(
  path: string,
): { route: LinkRoute; params: Record<string, string> } | null {
  const segments = splitSegments(path);
  for (const route of LINK_ROUTES) {
    const params = matchPattern(route.web, segments);
    if (params) return { route, params };
  }
  return null;
}

function isKnownHost(host: string, allowDevHosts: boolean): boolean {
  // Port off FIRST, for both checks. `www.flashka.ai:443` is the same host as
  // `www.flashka.ai` — stripping only on the dev branch made an explicit-port
  // production link a stranger. Anchored to a trailing `:digits` rather than
  // `split(':')` so an IPv6 literal is not truncated to `[`.
  const hostname = host.replace(/:\d+$/, '');
  if ((FLASHKA_LINK_HOSTS as readonly string[]).includes(hostname)) return true;
  if (!allowDevHosts) return false;
  return FLASHKA_DEV_LINK_HOST_PATTERNS.some((pattern) => pattern.test(hostname));
}

export interface ParseLinkOptions {
  /**
   * Also accept preview and localhost hosts. OFF by default, and that default
   * is a security boundary rather than a convenience choice: the association
   * files only ever list production, but this parser is ALSO reached by paths
   * the OS never vetted — a link pasted into the Join-group sheet, a
   * `flashkaapp://` URL any app can fire. Trusting `*.vercel.app` there would
   * let anyone host a page the app treats as first-party and opens in its own
   * in-app browser.
   *
   * Callers that genuinely want preview hosts (a dev build pointed at a
   * branch deploy) opt in explicitly — `flashka-app` passes `__DEV__`.
   */
  readonly allowDevHosts?: boolean;
}

/**
 * Is this an `https://` link on one of OUR hosts? Separate from
 * {@link parseFlashkaLink} because the two answers differ for exactly the case
 * that matters: a marketing or legal page IS our link and has no route, so the
 * app opens it in a browser rather than treating it as a stranger's URL.
 */
export function isFlashkaWebLink(raw: string, options: ParseLinkOptions = {}): boolean {
  const split = splitUrl(raw);
  if (!split) return false;
  const scheme = split.scheme.toLowerCase();
  if (scheme !== 'http' && scheme !== 'https') return false;
  return isKnownHost(split.host, options.allowDevHosts ?? false);
}

/**
 * The canonical `https://` page for a LEGACY deck-share link, or `null`.
 *
 * {@link parseFlashkaLink} refuses `/deck/<name>-share-01234` on purpose — it
 * is a web page resolving someone else's snapshot, not a deck the recipient
 * owns. But "refused" is not "not ours": the caller still has to send it to a
 * browser, and it has to do that for BOTH shapes. Answering only for `https://`
 * (which is all {@link isFlashkaWebLink} can do — it requires that scheme) left
 * the `flashkaapp://` twin looking like a stranger's URL, so it fell through to
 * expo-router and opened the deck route the refusal exists to block.
 *
 * Returns the rebuilt `https://` URL for the same reason the `web-only` rows
 * do: a hand-typed `flashkaapp://deck/x-share-01234` should still reach the
 * real page.
 */
export function legacyDeckShareWebUrl(
  raw: string,
  options: ParseLinkOptions = {},
): string | null {
  const split = splitUrl(raw);
  if (!split) return null;
  const scheme = split.scheme.toLowerCase();
  if (scheme === 'http' || scheme === 'https') {
    if (!isKnownHost(split.host, options.allowDevHosts ?? false)) return null;
  } else if (scheme !== APP_URL_SCHEME) {
    // A bare path (no scheme) is ours too — that is the shape expo-router's
    // native-intent hook hands over on some platforms.
    if (scheme) return null;
  }
  const path = split.path.length > 1 ? split.path.replace(/\/+$/, '') : split.path;
  const segments = splitSegments(path);
  if (segments.length !== 2 || segments[0] !== 'deck') return null;
  const deckId = safeDecode(segments[1]);
  if (!LEGACY_DECK_SHARE_RE.test(deckId)) return null;
  return `${FLASHKA_WEB_ORIGIN}/deck/${encodeURIComponent(deckId)}`;
}

/**
 * Resolve any incoming URL to the route it means, or `null` when it belongs to
 * someone else (another host, a marketing page, a path we have no row for).
 */
export function parseFlashkaLink(
  raw: string,
  options: ParseLinkOptions = {},
): ParsedFlashkaLink | null {
  const allowDevHosts = options.allowDevHosts ?? false;
  const split = splitUrl(raw);
  if (!split) return null;

  const scheme = split.scheme.toLowerCase();
  if (scheme === 'http' || scheme === 'https') {
    // An http(s) URL is ours only if the HOST says so — and an EMPTY host does
    // not say so. `https:///deck/x` and `https:/deck/x` both split to no
    // authority at all, and skipping the check on a falsy host let them through
    // as first-party links.
    if (!isKnownHost(split.host, allowDevHosts)) return null;
  } else if (scheme && scheme !== APP_URL_SCHEME) {
    return null;
  }

  const path = split.path.length > 1 ? split.path.replace(/\/+$/, '') : split.path;
  let matched = matchLinkPath(path);
  if (!matched) {
    // `/de/deck/x` — only after the un-prefixed match fails, so a real segment
    // that happens to spell a language code is never eaten.
    const segments = splitSegments(path);
    if (segments.length > 1 && MARKETING_PATH_LANGS.includes(segments[0])) {
      matched = matchLinkPath(`/${segments.slice(1).join('/')}`);
    }
  }
  if (!matched) return null;
  // `/deck/<name>-share-01234` is a LEGACY share link, not a deck of yours —
  // the app has no copy of it, and the web page is what resolves the snapshot.
  // Letting it match `deck` would open the deck screen on an id the recipient
  // does not have. See {@link LEGACY_DECK_SHARE_RE}.
  if (matched.route.kind === 'deck' && LEGACY_DECK_SHARE_RE.test(matched.params.deckId)) {
    return null;
  }

  const rawQuery = parseQueryString(split.query);
  const query: Record<string, string> = {};
  for (const key of matched.route.carry ?? []) {
    const value = rawQuery[key];
    if (value !== undefined && value !== '') query[key] = value;
  }

  return {
    route: matched.route,
    kind: matched.route.kind as LinkKind,
    params: matched.params,
    query,
    path: buildLinkPath(matched.route.kind as LinkKind, matched.params),
  };
}

/**
 * Substitute `:name` segments; the inverse of {@link matchLinkPath}.
 *
 * `@` is put back verbatim after encoding. It is a legal path character (RFC
 * 3986 `pchar`), every `/profile/@handle` link in the wild and in web's own
 * `navigate()` calls spells it literally, and `%40` in a link someone is about
 * to paste into a chat is just noise.
 */
export function buildLinkPath(
  kind: LinkKind,
  params: Readonly<Record<string, string>> = {},
): string {
  const route = linkRoute(kind);
  return route.web.replace(/:([A-Za-z0-9_]+)/g, (_whole, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`[deep-links] "${kind}" needs a "${name}" param`);
    }
    return encodeURIComponent(value).replace(/%40/g, '@');
  });
}

/**
 * The canonical absolute URL for a target — the ONE builder every share sheet,
 * invite and notification should go through, so a link's host and query shape
 * are decided in a single place.
 */
export function buildWebLink(
  kind: LinkKind,
  params: Readonly<Record<string, string>> = {},
  query: Readonly<Record<string, string | undefined>> = {},
  origin: string = FLASHKA_WEB_ORIGIN,
): string {
  const path = buildLinkPath(kind, params);
  const search = stringifyQuery(query);
  return `${origin.replace(/\/+$/, '')}${path}${search ? `?${search}` : ''}`;
}

/** The `flashkaapp://` form of the same target — for QA and for `adb`/`xcrun`. */
export function buildAppSchemeLink(
  kind: LinkKind,
  params: Readonly<Record<string, string>> = {},
  query: Readonly<Record<string, string | undefined>> = {},
): string {
  const path = buildLinkPath(kind, params);
  const search = stringifyQuery(query);
  return `${APP_URL_SCHEME}://${path.replace(/^\//, '')}${search ? `?${search}` : ''}`;
}
